/**
 * Friction Acknowledgements — Phase 2a (beta-readiness addition)
 * ──────────────────────────────────────────────────────────────
 * Append-only UX-state sink. Lets a reviewer mark a FrictionLog row
 * as "reviewed" so the same dismissed/overridden suggestion or repeat
 * dispute doesn't keep re-surfacing every week.
 *
 * SCOPE & GUARDRAILS:
 *   • Append-only. Never mutates a prior entry, never deletes.
 *   • Annotates ONLY — does NOT change canonical entities (suggestion
 *     outcome, dispute state, holdback state, override result).
 *   • Does NOT influence executionScore, dispatch, payouts, or any
 *     governance write path. Pure presentation memory.
 *   • Composite key includes the source row id so future re-occurrences
 *     of the same contractor / suggestion kind remain visible.
 *
 * This is the ONLY writer permitted in `src/data/observability/`.
 * Any new sink must be reviewed against the Phase 2a observation rule.
 */

export type FrictionAckSubjectKind =
  | "suggestion"          // SoftEnforcementSuggestion.id
  | "override_outcome"    // OverrideOutcomeRecord (jobId + observationId)
  | "repeat_dispute"      // contractorId
  | "repeat_reversal";    // contractorId

export interface FrictionAcknowledgement {
  /** Stable composite id: `${subjectKind}:${subjectId}` */
  id: string;
  subjectKind: FrictionAckSubjectKind;
  /** Identifier of the row being acknowledged (suggestion id, contractor id, etc.). */
  subjectId: string;
  acknowledgedBy: string;
  acknowledgedAt: string;          // ISO
  note?: string;                   // optional reviewer note (≤280 chars, sanitized below)
}

/** Strip any free-text PII / financial leakage before persisting. */
function sanitizeNote(input: string | undefined): string | undefined {
  if (!input) return undefined;
  const trimmed = input.trim().slice(0, 280);
  // Strip obvious money patterns and emails — defensive, not exhaustive.
  return trimmed
    .replace(/\$\s?\d[\d,]*(\.\d+)?/g, "[$]")
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]");
}

export function buildAckId(kind: FrictionAckSubjectKind, subjectId: string): string {
  return `${kind}:${subjectId}`;
}

/**
 * Append a new acknowledgement. If the same composite id already exists,
 * the prior entry is preserved unchanged and the new one is appended
 * after it (full audit trail of every review touch).
 */
export function appendAcknowledgement(
  existing: ReadonlyArray<FrictionAcknowledgement>,
  input: {
    subjectKind: FrictionAckSubjectKind;
    subjectId: string;
    acknowledgedBy: string;
    note?: string;
    now?: () => Date;
  },
): FrictionAcknowledgement[] {
  const at = (input.now ?? (() => new Date()))().toISOString();
  const entry: FrictionAcknowledgement = {
    id: buildAckId(input.subjectKind, input.subjectId),
    subjectKind: input.subjectKind,
    subjectId: input.subjectId,
    acknowledgedBy: input.acknowledgedBy || "unknown",
    acknowledgedAt: at,
    note: sanitizeNote(input.note),
  };
  return [...existing, entry];
}

/** Returns the most-recent acknowledgement for each composite id. */
export function indexLatestAcks(
  entries: ReadonlyArray<FrictionAcknowledgement>,
): Map<string, FrictionAcknowledgement> {
  const out = new Map<string, FrictionAcknowledgement>();
  entries.forEach((e) => {
    const prev = out.get(e.id);
    if (!prev || new Date(e.acknowledgedAt).getTime() > new Date(prev.acknowledgedAt).getTime()) {
      out.set(e.id, e);
    }
  });
  return out;
}

export function isAcknowledged(
  index: Map<string, FrictionAcknowledgement>,
  kind: FrictionAckSubjectKind,
  subjectId: string,
): boolean {
  return index.has(buildAckId(kind, subjectId));
}
